import { useApolloClient } from "@apollo/client";
import { useState } from "react";
import { getSearchObjectives } from "src/queries/objectives.graphql";

/**
 * Hook to fetch objectives when a branch is expanded
 * Uses Apollo client directly for more control over query execution
 * @param {Function} onCompleted - Callback function when data loads successfully
 * @returns {Array} [loadBranch function, query result]
 */
const useLoadObjectiveBranch = (onCompleted) => {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load objectives for a specific branch
   * @param {Object} options - Query options
   * @param {String[]} options.objectiveIds - Array of objective IDs to fetch
   * @param {Number} options.maxChildDepth - How deep to fetch child objectives
   * @param {Object} options.inputOverrides - Override default input values
   */
  const loadObjectiveBranchWithParams = async ({
    objectiveIds = [],
    maxChildDepth = 1,
    inputOverrides = {},
    excludeBaseObjectives = false,
  } = {}) => {
    setLoading(true);
    setError(null);

    try {
      const result = await client.query({
        query: getSearchObjectives,
        variables: {
          input: {
            filter: {
              objectiveIds,
              onlyTopLevelObjectives: false,
            },
            excludeBaseObjectives,
            includeChildObjectives: true,
            includeParentObjectives: false,
            maxChildDepth,
            ...inputOverrides,
          },
        },
        fetchPolicy: "network-only",
      });

      setLoading(false);

      if (onCompleted && result.data) {
        onCompleted(result.data, { isRootLoad: false });
      }

      return result;
    } catch (err) {
      setLoading(false);
      setError(err);
      throw err;
    }
  };

  return [loadObjectiveBranchWithParams, { loading, error }];
};

export default useLoadObjectiveBranch;
